"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";

const GOOGLE_SSO_ENABLED =
  process.env.NEXT_PUBLIC_GOOGLE_SSO_ENABLED === "true";

function friendlyAuthError(code: string | undefined | null): string {
  if (code === "AccessDenied") {
    return "This email isn't on the approved list. Use your United Playgrounds partner agency address.";
  }
  return code || "Something went wrong. Please try again.";
}

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function signInWithGoogle() {
    setError(null);
    setLoading(true);
    const result = await signIn("google", { redirect: false });
    if (result?.error) {
      setError(friendlyAuthError(result.error));
      setLoading(false);
    }
  }

  async function signInWithMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await signIn("nodemailer", { email, redirect: false });
    if (result?.error) {
      setError(friendlyAuthError(result.error));
    } else {
      setMagicSent(true);
    }
    setLoading(false);
  }

  if (magicSent) {
    return (
      <div className="text-center py-4">
        <div className="text-5xl mb-4">📬</div>
        <h2 className="font-heading text-xl mb-2">Check your inbox</h2>
        <p className="text-sm text-up-muted">
          We sent a sign-in link to <span className="font-medium">{email}</span>.
          Click it to get in.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {GOOGLE_SSO_ENABLED && (
        <>
          <button
            type="button"
            onClick={signInWithGoogle}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white border border-up-gray-light rounded-lg py-3 text-sm font-medium text-up-black hover:bg-up-off-white transition-colors disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.28 9.14 5.38 12 5.38z"
              />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 text-xs text-up-muted">
            <div className="h-px flex-1 bg-up-gray-light" />
            <span>or</span>
            <div className="h-px flex-1 bg-up-gray-light" />
          </div>
        </>
      )}

      <form onSubmit={signInWithMagicLink} className="space-y-3">
        <div>
          <label htmlFor="email" className="label">
            Work email
          </label>
          <input
            id="email"
            type="email"
            required
            placeholder="you@unitedplaygrounds.nl"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email}
          className="btn-up w-full"
        >
          {loading ? "Sending..." : "Send magic link"}
        </button>
      </form>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
